import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Checkbox } from "@/components/ui/checkbox";
import { PeriodView } from "./period-view";
import type { ChildBase } from "nautika-types";

interface ChildDetailSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  child: ChildBase;
  selectedPeriodIds: string[];
  initialMonth?: Date;
}

export function ChildDetailSheet({ open, onOpenChange, child, selectedPeriodIds, initialMonth }: ChildDetailSheetProps) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="h-[100vh] min-w-[800px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>
            Détails de {child.firstName} {child.lastName}
          </SheetTitle>
          <SheetDescription>Informations détaillées sur l'enfant et ses réservations</SheetDescription>
        </SheetHeader>

        <div className="space-y-6 p-4">
          {/* Section Périodes réservées */}
          <div>
            <PeriodView child={child} selectedPeriodIds={selectedPeriodIds} initialMonth={initialMonth} />
          </div>

          {/* Section Autorisations */}
          <div className="space-y-4">
            <div className="text-lg font-semibold">Autorisations</div>

            {/* Peut partir seul */}
            <div className="flex cursor-default flex-row items-start rounded-lg border p-4 select-none">
              <div className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center">
                <Checkbox
                  checked={child.leaveAlone}
                  disabled
                  className="h-full w-full data-[state=checked]:border-green-500 data-[state=checked]:bg-green-500"
                />
              </div>
              <div className="ml-3 flex flex-1 flex-col gap-y-0.5">
                <div className="text-base font-medium">Peut partir seul</div>
                <p className="text-sm text-muted-foreground">L'enfant est autorisé à quitter le centre sans être accompagné</p>
              </div>
            </div>

            {/* Droit à l'image */}
            <div className="flex cursor-default flex-row items-start rounded-lg border p-4 select-none">
              <div className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center">
                <Checkbox
                  checked={child.imageRights}
                  disabled
                  className="h-full w-full data-[state=checked]:border-green-500 data-[state=checked]:bg-green-500"
                />
              </div>
              <div className="ml-3 flex flex-col gap-y-0.5">
                <div className="text-base font-medium">Droit à l'image accordé</div>
                <p className="text-sm text-muted-foreground">Autorisation de photographier/filmer l'enfant pour les activités</p>
              </div>
            </div>
          </div>

          {/* Section Observations */}
          <div className="space-y-3">
            <div className="text-lg font-semibold">Observations</div>
            <div className="rounded-lg border bg-gray-50 p-4">
              {child.notes && child.notes.trim() ? (
                <p className="text-sm whitespace-pre-wrap">{child.notes}</p>
              ) : (
                <p className="text-sm text-gray-500 italic">
                  Aucune observation particulière n'a été renseignée pour cet enfant (allergies, médicaments, comportements spécifiques, etc.)
                </p>
              )}
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
